// client/src/components/StartingBidEditor.jsx
import React from "react";
import { buildStartingBidSchedule } from "../shared/startingBid";
import { formatMoney, localeForCountry } from "../lib/money";

export default function StartingBidEditor({
  form,
  values,
  setValues,
  rounding = "ceil",
  // IMPORTANT: never default currency/locale to hardcoded values.
  // Parent should pass currency={form.currency_code} and locale={localeForCountry(form.country_code)}.
  currency,
  locale,
}) {
  const duration = React.useMemo(() => Number(form.duration || 0), [form.duration]);
  const contribution = React.useMemo(() => Number(form.contribution || 0), [form.contribution]);
  const freq = React.useMemo(
    () => String(form.contribution_frequency || "monthly").toLowerCase(),
    [form.contribution_frequency]
  );
  const bidStep = React.useMemo(() => Number(form.bid_step || 100), [form.bid_step]);
  const organiserPeriod = React.useMemo(
    () => Number(form.organiser_benefit_period || 0),
    [form.organiser_benefit_period]
  );
  const annualRate = React.useMemo(
    () => Number(form.annual_interest_rate ?? 0.1),
    [form.annual_interest_rate]
  );

  const effectiveLocale = React.useMemo(() => {
    // If caller didn't pass locale, derive from country_code (or browser as fallback).
    return locale || localeForCountry(form?.country_code);
  }, [locale, form?.country_code]);

  const fmt = React.useCallback(
    (n) =>
      formatMoney(n, {
        currencyCode: currency, // may be empty; formatMoney will not guess
        locale: effectiveLocale,
        countryCode: form?.country_code,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
        fallback: "—",
      }),
    [currency, effectiveLocale, form?.country_code]
  );

  const computeRecommended = React.useCallback(() => {
    if (!duration || !contribution) return [];
    const rec = buildStartingBidSchedule({
      duration,
      contribution,
      frequency: freq,
      bidStep,
      annualRate,
      organiserPeriod,
      rounding,
    });
    return rec.map((r) => ({
      period: r.period,
      starting_bid: Math.max(0, Number(r.recommended)),
    }));
  }, [annualRate, bidStep, contribution, duration, freq, organiserPeriod, rounding]);

  React.useEffect(() => {
    if (!values || values.length === 0) setValues(computeRecommended());
  }, [computeRecommended, setValues, values]);

  const snapToStep = React.useCallback(
    (v) => {
      const s = Number(bidStep || 0);
      if (!s || s <= 0) return v;
      return Math.ceil(v / s) * s;
    },
    [bidStep]
  );

  const handleReset = React.useCallback(() => {
    setValues(computeRecommended());
  }, [computeRecommended, setValues]);

  const rows = Array.isArray(values) ? values : [];
  const lastPeriod = duration;

  return (
    <div className="space-y-3">
      <div className="text-sm text-gray-600">
        Edit any value. Period {organiserPeriod || "—"} and the last period ({lastPeriod || "—"})
        {" "}are locked at {fmt(0)}.
      </div>

      <table className="min-w-full border border-gray-200">
        <thead>
          <tr className="bg-gray-50">
            <th className="p-2 border">Period</th>
            <th className="p-2 border">Starting bid</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const p = Number(row.period);
            const mustZero = p === organiserPeriod || p === lastPeriod;
            const stepAttr = Number.isFinite(bidStep) && bidStep > 0 ? bidStep : 1;
            const displayVal = mustZero ? 0 : row.starting_bid || 0;

            return (
              <tr key={p}>
                <td className="p-2 border align-middle">
                  {p}
                  {mustZero ? " (locked)" : ""}
                </td>
                <td className="p-2 border">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      step={stepAttr}
                      value={displayVal}
                      disabled={mustZero}
                      onChange={(e) => {
                        const v = Number(e.target.value || 0);
                        const next = [...rows];
                        next[idx] = { ...row, starting_bid: v };
                        setValues(next);
                      }}
                      onBlur={() => {
                        if (!mustZero) {
                          const v = snapToStep(rows[idx].starting_bid);
                          const next = [...rows];
                          next[idx] = { ...row, starting_bid: v };
                          setValues(next);
                        }
                      }}
                      className="w-36 border px-2 py-1 rounded"
                    />
                    <span className="text-xs text-gray-500">{fmt(displayVal)}</span>
                  </div>
                </td>
              </tr>
            );
          })}

          {rows.length === 0 && (
            <tr>
              <td className="p-2 border text-sm text-gray-500" colSpan={2}>
                No rows yet. Fill in the form above to generate a schedule.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleReset}
          className="mt-1 px-3 py-1 border rounded hover:bg-gray-50"
        >
          Reset to recommended
        </button>

        <div className="text-xs text-gray-500">
          Step: {bidStep || 100} • Rounding: {rounding}
          <div className="mt-1">
            Note: if the interest-based floor is below the step, it will snap up to the step — so
            changing the interest rate may not visibly change the recommended values unless you use a
            smaller step.
          </div>
        </div>
      </div>
    </div>
  );
}
